/**
 * MysteriesPage — public research-leads marketplace (v0.1).
 *
 * Lists `propose_investigate_gap` leads from trees whose owner opted in.
 * Solvers filter by era / country / surname / place / gap type, click
 * "Try to solve" to land on the target person's profile where the
 * Origin agent (or manual research) can take it from there.
 *
 * Solver attribution back to the originating mystery is a follow-up —
 * the route already accepts `from_mystery_id`, the handoff just needs
 * the URL plumbing in place.
 */
import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { api } from '../services/api.js'
import { usePageTitle } from '../hooks/usePageTitle.js'
import { useAuthStore } from '../stores/auth.js'
import SolveQuestModal from '../components/mysteries/SolveQuestModal.jsx'

const PAGE_SIZE = 40
const COUNTRIES = [
  ['', 'Any country'],
  ['NL', 'Netherlands'], ['BE', 'Belgium'], ['DE', 'Germany'],
  ['FR', 'France'], ['GB', 'United Kingdom'], ['US', 'United States'],
]
const GAP_TYPES = [
  ['', 'Any gap type'],
  ['vertical',   'Vertical (parents / grandparents)'],
  ['horizontal', 'Horizontal (siblings / cousins)'],
  ['whitespot',  'Whitespot (archive coverage)'],
]

export default function MysteriesPage() {
  usePageTitle('Mysteries')

  const [view, setView] = useState('marketplace')  // 'marketplace' | 'community'
  const [filters, setFilters] = useState({
    era_from: '', era_to: '', country: '', surname: '', place: '',
    gap_type: '', min_confidence: '',
  })
  const [results, setResults] = useState({ mysteries: [], total: 0 })
  const [loading, setLoading] = useState(false)
  const [err, setErr]         = useState(null)
  const [offset, setOffset]   = useState(0)
  const [reputation, setReputation] = useState(null)
  const [featured, setFeatured]     = useState(null)

  useEffect(() => {
    api.get('/mysteries/reputation/me').then(setReputation).catch(() => {})
    api.get('/researcher/featured/current').then(setFeatured).catch(() => {})
  }, [])

  // Debounced fetch on filter / view change.
  useEffect(() => {
    const t = setTimeout(() => fetchMysteries({ ...filters, view, offset, limit: PAGE_SIZE }), 250)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, view, offset])

  function changeView(v) {
    setView(v)
    setOffset(0)
  }

  async function fetchMysteries(params) {
    setLoading(true)
    setErr(null)
    try {
      const qs = Object.entries(params)
        .filter(([_, v]) => v !== '' && v != null)
        .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
        .join('&')
      const data = await api.get(`/mysteries?${qs}`)
      setResults(data)
    } catch (e) {
      setErr(e.message || 'Failed to load mysteries')
    } finally {
      setLoading(false)
    }
  }

  function update(field, value) {
    setOffset(0)
    setFilters(f => ({ ...f, [field]: value }))
  }

  function reset() {
    setOffset(0)
    setFilters({ era_from: '', era_to: '', country: '', surname: '', place: '', gap_type: '', min_confidence: '' })
  }

  const pageStart = offset + 1
  const pageEnd   = Math.min(offset + PAGE_SIZE, results.total)

  return (
    <div className="page mysteries-page">
      <header className="mysteries-page__header">
        <div>
          <div className="mysteries-page__eyebrow">Origin AI · marketplace</div>
          <h1 className="mysteries-page__title">Mysteries</h1>
          <p className="mysteries-page__intro">
            Research leads shared by other family historians. Pick one, dig in, and
            file what you find — the originator reviews and credits your work.
          </p>
        </div>
        {reputation && (
          <div className="mysteries-page__rep">
            <div className="mysteries-page__rep-row">
              <span>{reputation.mysteries_solved || 0}</span> solved
            </div>
            <div className="mysteries-page__rep-row">
              <span>{reputation.mysteries_attempted || 0}</span> attempted
            </div>
            <div className="mysteries-page__rep-row mysteries-page__rep-row--muted">
              {reputation.approved_total || 0} total approved suggestions
            </div>
            <Link to="/leaderboard" className="mysteries-page__rep-link">
              Leaderboard →
            </Link>
          </div>
        )}
      </header>

      {featured && (
        <FeaturedSolverStrip featured={featured} />
      )}

      <nav className="mysteries-page__tabs" role="tablist">
        <button
          role="tab"
          aria-selected={view === 'marketplace'}
          className={`mysteries-page__tab ${view === 'marketplace' ? 'is-active' : ''}`}
          onClick={() => changeView('marketplace')}
        >
          <strong>Marketplace</strong>
          <span className="mysteries-page__tab-sub">Bountied & promoted leads</span>
        </button>
        <button
          role="tab"
          aria-selected={view === 'community'}
          className={`mysteries-page__tab ${view === 'community' ? 'is-active' : ''}`}
          onClick={() => changeView('community')}
        >
          <strong>Community board</strong>
          <span className="mysteries-page__tab-sub">Volunteer-mode browse</span>
        </button>
      </nav>

      <section className="mysteries-page__filters">
        <input
          placeholder="Surname"
          value={filters.surname}
          onChange={e => update('surname', e.target.value)}
        />
        <input
          placeholder="Place"
          value={filters.place}
          onChange={e => update('place', e.target.value)}
        />
        <select value={filters.country} onChange={e => update('country', e.target.value)}>
          {COUNTRIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <select value={filters.gap_type} onChange={e => update('gap_type', e.target.value)}>
          {GAP_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <input
          type="number" placeholder="Era from" min="1300" max="2025"
          value={filters.era_from}
          onChange={e => update('era_from', e.target.value)}
        />
        <input
          type="number" placeholder="Era to" min="1300" max="2025"
          value={filters.era_to}
          onChange={e => update('era_to', e.target.value)}
        />
        <select
          value={filters.min_confidence}
          onChange={e => update('min_confidence', e.target.value)}
        >
          <option value="">Any confidence</option>
          <option value="0.5">≥ 0.50</option>
          <option value="0.7">≥ 0.70</option>
          <option value="0.85">≥ 0.85</option>
        </select>
        <button onClick={reset} className="mysteries-page__reset">Reset</button>
      </section>

      {err && <div className="mysteries-page__error">{err}</div>}

      {!err && (
        <div className="mysteries-page__meta">
          {loading
            ? 'Loading…'
            : results.total === 0
              ? (view === 'marketplace'
                  ? 'No bountied or promoted mysteries right now. Switch to the Community board to see all open leads.'
                  : 'No community-board mysteries match those filters. Try widening them, or check back as more trees opt in.')
              : `Showing ${pageStart}–${pageEnd} of ${results.total}`}
        </div>
      )}

      <ol className="mysteries-page__list">
        {results.mysteries.map(m => (
          <MysteryCard key={m.id} mystery={m} />
        ))}
      </ol>

      {results.total > PAGE_SIZE && (
        <nav className="mysteries-page__pager">
          <button disabled={offset === 0 || loading} onClick={() => setOffset(o => Math.max(0, o - PAGE_SIZE))}>← Previous</button>
          <span>{pageStart}–{pageEnd} / {results.total}</span>
          <button disabled={offset + PAGE_SIZE >= results.total || loading} onClick={() => setOffset(o => o + PAGE_SIZE)}>Next →</button>
        </nav>
      )}
    </div>
  )
}

function FeaturedSolverStrip({ featured }) {
  return (
    <section className="featured-solver">
      <div className="featured-solver__eyebrow">★ Featured researcher this week</div>
      <div className="featured-solver__body">
        <div className="featured-solver__name">
          {featured.public_profile_slug
            ? <Link to={`/researcher/${featured.public_profile_slug}`}>{featured.name}</Link>
            : featured.name}
          {featured.tier === 'researcher' && <span className="featured-solver__pro" title="Pro Researcher">👑</span>}
        </div>
        {featured.headline && <div className="featured-solver__headline">{featured.headline}</div>}
        {featured.blurb     && <p className="featured-solver__blurb">{featured.blurb}</p>}
      </div>
    </section>
  )
}

function MysteryCard({ mystery }) {
  const navigate = useNavigate()
  const [solving, setSolving] = useState(false)
  const p = mystery.payload || {}
  const kind = mystery.kind || 'investigate_gap'
  const isConfirm = kind === 'confirm_fact'
  const isPhoto   = kind === 'find_photo'
  const isSubmitQuest = isConfirm || isPhoto
  const personName = [mystery.person_first_name, mystery.person_last_name].filter(Boolean).join(' ') || null
  const personYear = (mystery.person_birth_date || '').slice(0, 4)
  const gapBadge   = isConfirm ? 'Verify a fact'
    : isPhoto ? 'Find a photo'
    : (({ vertical: 'Vertical', horizontal: 'Horizontal', whitespot: 'Whitespot' })[p.gap_type] || 'Lead')
  const chipMod    = isConfirm ? 'confirm' : isPhoto ? 'photo' : (p.gap_type || 'other')
  const cardTitle  = isSubmitQuest
    ? (p.title || p.description || gapBadge)
    : (p.description || mystery.note || 'Research lead')
  const confPct    = Math.round(Number(mystery.confidence || 0) * 100)
  const bounty     = mystery.active_bounty
  const bountyEur  = bounty ? (bounty.amount_cents / 100).toFixed(0) : null

  function trySolve() {
    // confirm_fact / find_photo answers are submitted in a modal here; gaps
    // hand off to the person page where Origin or manual research takes over.
    if (isSubmitQuest) {
      setSolving(true)
      return
    }
    if (mystery.person_id && mystery.tree_id) {
      navigate(`/tree/${mystery.tree_id}/person/${mystery.person_id}?fromMystery=${mystery.id}`)
    } else {
      navigate(`/tree/${mystery.tree_id}?fromMystery=${mystery.id}`)
    }
  }

  return (
    <li className="mysteries-card">
      <div className="mysteries-card__head">
        <span className={`mysteries-card__chip mysteries-card__chip--${chipMod}`}>{gapBadge}</span>
        {confPct > 0 && (
          <span className="mysteries-card__chip mysteries-card__chip--conf">{confPct}% confidence</span>
        )}
        {(mystery.solver_attempts > 0) && (
          <span className="mysteries-card__chip mysteries-card__chip--solvers">
            {mystery.solver_solves}/{mystery.solver_attempts} solved
          </span>
        )}
        {mystery.pro_attempts > 0 && (
          <span className="mysteries-card__chip mysteries-card__chip--pro" title="Researcher-tier solver attempting">
            👑 {mystery.pro_attempts} Pro
          </span>
        )}
        {bounty && (
          <span className="mysteries-card__chip mysteries-card__chip--bounty">
            €{bountyEur} bounty
          </span>
        )}
        {!bounty && mystery.promoted_to_marketplace && (
          <span className="mysteries-card__chip mysteries-card__chip--promoted" title="Promoted by owner — no bounty">
            ★ Promoted
          </span>
        )}
      </div>

      <h3 className="mysteries-card__title">
        {cardTitle}
      </h3>

      <div className="mysteries-card__meta">
        {personName && (
          <span>About <strong>{personName}</strong>{personYear ? ` (b. ${personYear})` : ''}</span>
        )}
        {isConfirm && p.field && (
          <span>· Verify <strong>{p.field.replace(/_/g, ' ')}</strong>{p.current_value ? `: “${p.current_value}”` : ''}</span>
        )}
        {p.likely_era   && <span>· Era: {p.likely_era}</span>}
        {p.likely_place && <span>· Place: {p.likely_place}</span>}
        {mystery.tree_name && <span>· Tree: <em>{mystery.tree_name}</em></span>}
      </div>

      {p.rationale && (
        <p className="mysteries-card__rationale">{p.rationale}</p>
      )}

      <div className="mysteries-card__actions">
        {mystery.viewer_is_owner ? (
          // Owners don't act on their own leads from here — this is the
          // public marketplace surface. Read-only status only. Triage
          // (promote / bounty / hide) lives on the Suggestions Inbox.
          <span className="mysteries-card__owner-note">
            {bounty
              ? <>Your bounty is live · €{bountyEur}</>
              : mystery.promoted_to_marketplace
                ? <>★ Promoted by you</>
                : <>Your lead</>}
            {' · '}
            <Link to="/suggestions" className="mysteries-card__owner-note-link">Manage in inbox →</Link>
          </span>
        ) : (
          <button className="btn btn-primary" onClick={trySolve}>
            {bounty
              ? `Solve to earn €${bountyEur} →`
              : isConfirm ? 'Verify this fact →'
              : isPhoto ? 'Find a photo →'
              : 'Try to solve →'}
          </button>
        )}
        {mystery.research_query_id && !mystery.viewer_is_owner && (
          <Link className="mysteries-card__sub" to={`/suggestions`}>
            View Origin's original investigation
          </Link>
        )}
      </div>
      {solving && (
        <SolveQuestModal
          mystery={mystery}
          onClose={() => setSolving(false)}
          onSolved={() => { /* leave the card; owner reviews the answer */ }}
        />
      )}
    </li>
  )
}
